import { Icon } from "./icons";
import { explorerUrl, shortHash } from "../lib/format";
import type { Payout } from "../lib/types";

/**
 * The receipt for a payout, or an honest blank.
 *
 * A paid payout links to the transfer on a block explorer; anything else shows
 * a dash. The temptation is to show the destination address on every row
 * instead, which is what the PayPal version did with the email — but the
 * address is the same on every row and already at the top of this page, so a
 * column of it repeats one fact and answers nothing.
 *
 * An unknown chain renders the hash without a link rather than guessing at an
 * explorer. A link to the wrong chain's explorer says "not found", which reads
 * as the payment not existing.
 */
export function TxLink({ payout }: { payout: Payout }) {
  if (!payout.txHash) return <span className="text-[12px] text-fg-subtle">—</span>;

  const href = explorerUrl(payout.chain, payout.txHash);
  if (!href) {
    return (
      <span className="font-mono text-[12px] text-fg-muted">{shortHash(payout.txHash)}</span>
    );
  }

  return (
    <a
      className="inline-flex items-center gap-1 font-mono text-[12px] whitespace-nowrap text-accent transition-colors duration-200 ease-smooth hover:underline"
      href={href}
      target="_blank"
      rel="noreferrer"
      title={payout.txHash}
    >
      {shortHash(payout.txHash)}
      <Icon name="external" className="size-3" />
    </a>
  );
}
